import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CanvasActionBar } from "@/components/canvas/canvas-action-bar";
import { CanvasCardItem } from "@/components/canvas/canvas-card";
import {
	CanvasChatOpenButton,
	CanvasChatPanel,
} from "@/components/canvas/canvas-chat-panel";
import { CanvasCommentMarker } from "@/components/canvas/canvas-comment-marker";
import { CanvasCommentsPanel } from "@/components/canvas/canvas-comments-panel";
import { CanvasPendingComment } from "@/components/canvas/canvas-pending-comment";
import { CanvasUploadModal } from "@/components/canvas/canvas-upload-modal";
import {
	type CanvasCard,
	type CanvasTool,
	toolResultsToCards,
} from "@/components/canvas/canvas-utils";
import { CanvasZoomControls } from "@/components/canvas/canvas-zoom-controls";
import { useCanvasChat } from "@/components/canvas/use-canvas-chat";
import { useCanvasComments } from "@/components/canvas/use-canvas-comments";
import { useCanvasInteractions } from "@/components/canvas/use-canvas-interactions";
import { useCanvasTools } from "@/components/canvas/use-canvas-tools";
import { useCanvasViewport } from "@/components/canvas/use-canvas-viewport";
import { ProjectHeader } from "@/components/layout/project-header";
import { SocialPlatformModal } from "@/components/social-platform-modal";
import { useAgentQuestions } from "@/hooks/use-agent-questions";
import { useDeletionAnswer } from "@/hooks/use-deletion-answer";
import {
	api,
	type ChatHistoryMessage,
	type Project,
	type ToolResult,
} from "@/lib/api";

// Stable fallbacks: a fresh `[]` each render would re-fire every effect that
// depends on these lists (e.g. chat auto-scroll) while data loads.
const NO_MESSAGES: ChatHistoryMessage[] = [];
const NO_TOOL_RESULTS: ToolResult[] = [];

export function CanvasPage() {
	const navigate = useNavigate();
	const { projectId } = useParams<{ projectId: string }>();
	const [searchParams, setSearchParams] = useSearchParams();

	// --- Layout State ---
	const [isChatOpen, setIsChatOpen] = useState(true);
	const [isModalOpen, setIsModalOpen] = useState(false);

	// --- Canvas Tool Mode ---
	const [canvasTool, setCanvasTool] = useState<CanvasTool>("hand");

	// API queries — same endpoint as WorkspacePage
	const { data: chatMessages = NO_MESSAGES, isLoading: isLoadingChat } = useQuery<
		ChatHistoryMessage[]
	>({
		queryKey: ["chatHistory", projectId],
		queryFn: () => api.getChatHistory(projectId!),
		enabled: !!projectId,
	});

	const { pendingQuestion, clearQuestion, resync } = useAgentQuestions(projectId);
	const deletion = useDeletionAnswer({ projectId, clearQuestion, resyncQuestion: resync });

	const { data: projectDetails } = useQuery<Project>({
		queryKey: ["projectDetails", projectId],
		queryFn: () => api.getProjectDetails(projectId!),
		enabled: !!projectId,
	});

	const { data: toolResults = NO_TOOL_RESULTS, isLoading: isLoadingTools } = useQuery<ToolResult[]>({
		queryKey: ["toolResults", projectId],
		queryFn: () => api.getToolResults(projectId!),
		enabled: !!projectId,
	});

	// --- Studio tools (picker, Content Plan platform modal, ?tool= auto-trigger) ---
	const {
		isToolPickerOpen,
		setIsToolPickerOpen,
		loadingTool,
		isPlatformModalOpen,
		setIsPlatformModalOpen,
		selectedPlatform,
		setSelectedPlatform,
		handleToolClick,
		executeToolWithPlatform,
	} = useCanvasTools({
		projectId,
		toolResults,
		isLoadingTools,
		searchParams,
		setSearchParams,
	});

	// --- Canvas State ---
	const [cards, setCards] = useState<CanvasCard[]>([]);

	// Sync tool results → cards
	useEffect(() => {
		if (toolResults.length > 0) {
			setCards(toolResultsToCards(toolResults));
		}
	}, [toolResults]);

	// --- Chat ---
	const {
		chatInput,
		setChatInput,
		chatEndRef,
		isSending,
		handleAgentOptionSelect,
		handleSendMessage,
	} = useCanvasChat({ projectId, chatMessages, clearQuestion, resyncQuestion: resync });

	// --- Viewport (pan / zoom) ---
	const {
		canvasRef,
		canvasOffset,
		setCanvasOffset,
		zoom,
		setZoom,
		zoomPercent,
		handleZoomIn,
		handleZoomOut,
		handleResetView,
		handleFitAll,
	} = useCanvasViewport({ cards, isChatOpen });

	// --- Comments ---
	const {
		isCommentMode,
		comments,
		pendingComment,
		commentInput,
		setCommentInput,
		activeCommentId,
		hoveredCommentId,
		setHoveredCommentId,
		editingCommentId,
		editingText,
		setEditingText,
		commentInputRef,
		editInputRef,
		handleCommentToggle,
		placePendingComment,
		cancelPendingComment,
		handleCommentSubmit,
		handleResolveComment,
		handleMarkerClick,
		handleStartEdit,
		cancelEdit,
		handleSaveEdit,
		handleFocusComment,
	} = useCanvasComments({
		projectId,
		canvasRef,
		zoom,
		setZoom,
		setCanvasOffset,
	});

	// --- Pointer gestures (pan, card drag / resize, comment placement) ---
	const {
		handleCanvasMouseDown,
		handleCanvasMouseMove,
		handleCanvasMouseUp,
		handleCardDragStart,
		handleResizeStart,
	} = useCanvasInteractions({
		canvasRef,
		zoom,
		canvasOffset,
		setCanvasOffset,
		setCards,
		canvasTool,
		isCommentMode,
		placePendingComment,
	});

	const handleOpenEditor = (card: CanvasCard) =>
		navigate(`/editor/${projectId}`, {
			state: { title: card.title, content: card.content },
		});

	return (
		<div
			className="flex flex-col h-screen overflow-hidden absolute inset-0"
			data-canvas-bg
			style={{
				backgroundColor: "var(--bg-canvas)",
			}}
		>
			<ProjectHeader
				projectTitle={projectDetails?.title || "Canvas"}
				isCommentMode={isCommentMode}
				onCommentToggle={handleCommentToggle}
			/>

			<div className="flex flex-1 overflow-hidden relative">
				<CanvasChatPanel
					isOpen={isChatOpen}
					onClose={() => setIsChatOpen(false)}
					messages={chatMessages}
					isLoading={isLoadingChat}
					isSending={isSending}
					pendingQuestion={pendingQuestion}
					onAgentOptionSelect={handleAgentOptionSelect}
					onDeletionChoice={(choice) => deletion.answer(pendingQuestion?.questionId, choice)}
					isAnsweringDeletion={deletion.isAnswering}
					chatInput={chatInput}
					onChatInputChange={setChatInput}
					onSubmit={handleSendMessage}
					chatEndRef={chatEndRef}
				/>
				<div
					ref={canvasRef}
					className="flex-1 relative overflow-hidden select-none bg-bg-canvas"
					style={{
						backgroundImage: "radial-gradient(var(--border-control) 1px, transparent 1px)",
						backgroundSize: "20px 20px",
						cursor: isCommentMode
							? "crosshair"
							: canvasTool === "hand"
								? "grab"
								: "default",
					}}
					onMouseDown={handleCanvasMouseDown}
					onMouseMove={handleCanvasMouseMove}
					onMouseUp={handleCanvasMouseUp}
					onMouseLeave={handleCanvasMouseUp}
					onWheel={(e) => e.stopPropagation()}
				>

					<div
						className="absolute top-0 left-0"
						style={{
							transform: `translate(${canvasOffset.x}px, ${canvasOffset.y}px) scale(${zoom})`,
							transformOrigin: "0 0",
							willChange: "transform",
						}}
					>
						{cards.map((card, i) => (
							<CanvasCardItem
								key={card.id}
								card={card}
								index={i}
								isCommentMode={isCommentMode}
								onDragStart={handleCardDragStart}
								onResizeStart={handleResizeStart}
								onOpenEditor={handleOpenEditor}
							/>
						))}

						{/* ── Comment Markers ── */}
						{comments.map((comment) => (
							<CanvasCommentMarker
								key={comment.id}
								comment={comment}
								zoom={zoom}
								isExpanded={
									activeCommentId === comment.id ||
									hoveredCommentId === comment.id
								}
								onHoverChange={setHoveredCommentId}
								onMarkerClick={handleMarkerClick}
								isEditing={editingCommentId === comment.id}
								editingText={editingText}
								onEditingTextChange={setEditingText}
								editInputRef={editInputRef}
								onSaveEdit={handleSaveEdit}
								onCancelEdit={cancelEdit}
								onStartEdit={handleStartEdit}
							/>
						))}

						{/* ── Pending Comment Input ── */}
						<CanvasPendingComment
							pendingComment={pendingComment}
							zoom={zoom}
							commentInput={commentInput}
							onCommentInputChange={setCommentInput}
							commentInputRef={commentInputRef}
							onSubmit={handleCommentSubmit}
							onCancel={cancelPendingComment}
						/>
					</div>

					{/* ── Toggle Chat Button (when closed) ── */}
					{!isChatOpen && (
						<CanvasChatOpenButton onOpen={() => setIsChatOpen(true)} />
					)}

					{/* ── Bottom Action Bar ── (fixed overlay, not transformed) */}
					<CanvasActionBar
						isChatOpen={isChatOpen}
						setIsChatOpen={setIsChatOpen}
						isToolPickerOpen={isToolPickerOpen}
						setIsToolPickerOpen={setIsToolPickerOpen}
						isModalOpen={isModalOpen}
						setIsModalOpen={setIsModalOpen}
						onToolClick={handleToolClick}
						loadingTool={loadingTool}
					/>

					{/* ── Bottom Right: Zoom Controls + Status ── */}
					<CanvasZoomControls
						canvasTool={canvasTool}
						setCanvasTool={setCanvasTool}
						zoomPercent={zoomPercent}
						onZoomIn={handleZoomIn}
						onZoomOut={handleZoomOut}
						onResetView={handleResetView}
						onFitAll={handleFitAll}
					/>
				</div>

				{/* ═══════════════════ RIGHT COMMENT SIDEBAR ═══════════════════ */}
				<CanvasCommentsPanel
					isOpen={isCommentMode}
					onClose={handleCommentToggle}
					comments={comments}
					activeCommentId={activeCommentId}
					editingCommentId={editingCommentId}
					editingText={editingText}
					onEditingTextChange={setEditingText}
					onSaveEdit={handleSaveEdit}
					onCancelEdit={cancelEdit}
					onFocusComment={handleFocusComment}
					onResolveComment={handleResolveComment}
				/>
			</div>

			{/* ═══════════════════ FILE UPLOAD MODAL ═══════════════════ */}
			<CanvasUploadModal
				open={isModalOpen}
				onClose={() => setIsModalOpen(false)}
			/>

			{/* Platform selection for Content Plan */}
			<SocialPlatformModal
				open={isPlatformModalOpen}
				onClose={() => setIsPlatformModalOpen(false)}
				selected={selectedPlatform}
				onSelect={setSelectedPlatform}
				onContinue={executeToolWithPlatform}
				subtitle="Select social media platform"
			/>
		</div>
	);
}
